/**
 * Admin-side seed for the "Require team name" toggle (ADR 0049) — mirrors the
 * public resolver (apps/app/app/utils/resolveExcludeTeamName.ts) minus the
 * division tier, which the registration edit page can't see or edit. Kept
 * admin-local rather than shared: the public resolver depends on
 * resolveDivisionConfig and is parity-pinned in apps/next-app, so moving it
 * would touch both ports for no admin gain.
 *
 * Resolution: explicit parent (tournament/league — the authoritative ADR 0034
 * tier) > legacy explicit registration.config > individual-sport default
 * (true = no team name) > CONFIG_DEFAULTS (false). Only real booleans shadow;
 * absent/undefined means "inherit".
 */
import { isIndividualSport } from '@staty/shared/utils/sportCategory';
import { CONFIG_DEFAULTS } from '@staty/shared/utils/configDefaults';

interface SeedArgs {
  registrationConfig?: Record<string, unknown> | null;
  parentConfig?: Record<string, unknown> | null;
  sport?: string | null;
}

/**
 * What excludeTeamName resolves to with NO explicit value anywhere — the
 * baseline the save pipeline compares against (no-stamping: an explicit
 * boolean is persisted only when it differs from this).
 */
export function sportDefaultExcludeTeamName(sport?: string | null): boolean {
  return sport && isIndividualSport(sport) ? true : (CONFIG_DEFAULTS.excludeTeamName as boolean);
}

export default function resolveExcludeTeamNameSeed({
  registrationConfig,
  parentConfig,
  sport,
}: SeedArgs = {}): boolean {
  const parent = parentConfig?.excludeTeamName;
  if (typeof parent === 'boolean') return parent;

  const legacy = registrationConfig?.excludeTeamName;
  if (typeof legacy === 'boolean') return legacy;

  return sportDefaultExcludeTeamName(sport);
}
